import { Space } from "antd";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { DataTableDateColumn } from "~components/DataTable/components/DataTableDateColumn/DataTableDateColumn.tsx";
import { DataTableSelectColumn } from "~components/DataTable/components/DataTableSelectColumn/DataTableSelectColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import ImageView from "~components/ImageView/ImageView.tsx";
import { dateFormat } from "~constants/dateFormat.ts";
import { ImageSizes } from "~enums/imageSizes.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import type { RoleStatuses } from "~enums/roleStatuses.ts";
import type { Roles } from "~enums/roles.ts";
import { StaffTableColumns } from "~pages/Staff/StaffListPage/enums/staffTableColumns.ts";
import type { StaffListItem } from "~pages/Staff/StaffListPage/types/staffListItem.ts";
import type { UpdateStaffRole } from "~pages/Staff/types/staffRole.ts";
import { getRoleTranslationKey } from "~utilities/getRoleTranslationKey.ts";
import { sortStringValues } from "~utils/utilities.ts";

type StaffRow = StaffListItem & DataTableRow;

type Props = {
  massEditMode: boolean;
  onUpdateRole: (row: StaffRow, update: Partial<Pick<UpdateStaffRole, "role" | "status">>) => Promise<void>;
  roleStatuses: RoleStatuses[];
  staffRoles: Roles[];
};

export const useGetStaffTableColumns = ({
  massEditMode,
  onUpdateRole,
  roleStatuses,
  staffRoles,
}: Props): Array<DataTableColumn<StaffListItem>> => {
  const { formatMessage } = useIntl();
  const { sport = "" } = useParams();

  const roleOptions = staffRoles.map((role) => ({
    label: formatMessage({ id: `roles.${getRoleTranslationKey(role, sport)}` }),
    value: role,
  }));

  const statusOptions = roleStatuses.map((status) => ({
    label: formatMessage({ id: `roleStatus.${status}` }),
    value: status,
  }));

  const formatRole = (role: string) => formatMessage({ id: `roles.${getRoleTranslationKey(role as Roles, sport)}` });
  const formatStatus = (status: string) => formatMessage({ id: `roleStatus.${status}` });

  return [
    {
      dataIndex: "nameFullLocal",
      ellipsis: { showTitle: true },
      key: StaffTableColumns.NameFullLocal,
      render: (value: string, row: StaffListItem) => (
        <Space size="small">
          <ImageView imageUrl={row.avatarUrl} size={ImageSizes.Small} width={25} height={25} imageType={ImageTypes.PersonHead} />
          {value}
        </Space>
      ),
      sorter: (a: StaffListItem, b: StaffListItem) => sortStringValues(a.nameFullLocal, b.nameFullLocal),
      title: formatMessage({ id: "staff.table.column.nameFullLocal" }),
      width: 240,
    },
    {
      copyable: true,
      dataIndex: "personId",
      key: StaffTableColumns.PersonId,
      title: formatMessage({ id: "staff.table.column.personId" }),
      width: 260,
    },
    {
      copyable: true,
      dataIndex: "externalId",
      key: StaffTableColumns.ExternalId,
      title: formatMessage({ id: "staff.table.column.externalId" }),
      width: 180,
    },
    {
      copyable: true,
      dataIndex: "personExternalId",
      key: StaffTableColumns.PersonExternalId,
      title: formatMessage({ id: "staff.table.column.personExternalId" }),
      width: 180,
    },
    {
      dataIndex: "role",
      key: StaffTableColumns.Role,
      render: (value: string, row: StaffRow) => (
        <DataTableSelectColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          options={roleOptions}
          formatValue={formatRole}
          onSave={(role) => {
            if (!role) {
              return;
            }
            return onUpdateRole(row, { role: role as Roles });
          }}
        />
      ),
      sorter: (a: StaffListItem, b: StaffListItem) => sortStringValues(formatRole(a.role), formatRole(b.role)),
      title: formatMessage({ id: "staff.table.column.role" }),
      width: 200,
    },
    {
      dataIndex: "gender",
      key: StaffTableColumns.Gender,
      title: formatMessage({ id: "staff.table.column.gender" }),
      width: 120,
    },
    {
      dataIndex: "status",
      key: StaffTableColumns.Status,
      render: (value: string, row: StaffRow) => (
        <DataTableSelectColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          options={statusOptions}
          formatValue={formatStatus}
          onSave={(status) => {
            if (!status) {
              return;
            }
            return onUpdateRole(row, { status: status as RoleStatuses });
          }}
        />
      ),
      sorter: (a: StaffListItem, b: StaffListItem) => sortStringValues(formatStatus(a.status), formatStatus(b.status)),
      title: formatMessage({ id: "staff.table.column.status" }),
      width: 160,
    },
    {
      dataIndex: "nationality",
      key: StaffTableColumns.Nationality,
      title: formatMessage({ id: "staff.table.column.nationality" }),
      width: 140,
    },
    {
      dataIndex: "dateOfBirth",
      key: StaffTableColumns.DateOfBirth,
      render: (value: string | null) => <DataTableDateColumn value={value} format={dateFormat} />,
      title: formatMessage({ id: "staff.table.column.dateOfBirth" }),
      width: 140,
    },
  ];
};
